import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Plus,
  GripVertical,
  Pencil,
  Trash2,
  Calendar,
  ChevronDown,
  ChevronRight,
  ListTodo,
  X,
  Clock,
  ListPlus,
  Check,
} from "lucide-react";
import type { Milestone, Task, SubTask, Category } from "@shared/schema";
import { useState, useMemo, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { apiRequest, queryClient, toastTaskMutationError } from "@/lib/queryClient";
import { capturePersonalCacheBoundary } from "@/lib/personal-cache-boundary";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { formatDateInput } from "@/lib/dateUtils";
import {
  goalTaskFormSchema as taskFormSchema,
  type GoalTaskFormData as TaskFormData,
  type GoalTaskFormInput as TaskFormInput,
} from "@/lib/goalTaskUtils";

export interface SortableMilestoneItemProps {
  milestone: Milestone;
  goalId: string;
  onToggle: (id: string, achieved: boolean) => void;
  onDelete: (id: string) => void;
  /**
   * Optional external edit handler. When provided the edit button calls this
   * callback instead of opening the built-in inline edit dialog. GoalCard uses
   * this to manage edits through its own dialog; GoalOverviewTab omits it and
   * relies on the built-in dialog.
   */
  onEdit?: (milestone: Milestone) => void;
}

// Form schema for linking existing task
const linkTaskFormSchema = z.object({
  taskId: z.string().min(1, "Please select a task to link"),
});

type LinkTaskFormInput = z.input<typeof linkTaskFormSchema>;
type LinkTaskFormData = z.output<typeof linkTaskFormSchema>;

export function SortableMilestoneItem({
  milestone,
  goalId,
  onToggle,
  onDelete,
  onEdit,
}: SortableMilestoneItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [subtasks, setSubtasks] = useState<string[]>([]);
  const [newSubtask, setNewSubtask] = useState("");
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [dialogSubTaskText, setDialogSubTaskText] = useState("");
  const [milestoneSentToTasks, setMilestoneSentToTasks] = useState(false);
  const { toast } = useToast();

  // Task creation form
  const form = useForm<TaskFormInput, any, TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: "",
      description: "",
      dueDate: "",
      showOnMainTaskPage: true,
      category: "Uncategorized",
    },
  });

  // Link task form
  const linkForm = useForm<LinkTaskFormInput, any, LinkTaskFormData>({
    resolver: zodResolver(linkTaskFormSchema),
    defaultValues: {
      taskId: "",
    },
  });

  // Task edit form for the full-page dialog
  const taskEditForm = useForm<TaskFormInput, any, TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: "",
      description: "",
      dueDate: "",
      showOnMainTaskPage: true,
      category: "Uncategorized",
    },
  });

  // Update task edit form when selectedTask changes
  useEffect(() => {
    if (selectedTask) {
      taskEditForm.reset({
        title: selectedTask.title,
        description: selectedTask.description || "",
        dueDate: selectedTask.dueDate ? formatDateInput(new Date(selectedTask.dueDate)) : "",
        showOnMainTaskPage: selectedTask.showOnMainTaskPage ?? true,
        category: selectedTask.category || "Uncategorized",
      });
    }
  }, [selectedTask, taskEditForm]);

  // Edit milestone form (only used when onEdit is not provided)
  const editMilestoneSchema = z.object({
    title: z.string().min(1, "Title is required"),
    dueDate: z.string().optional(),
  });

  type EditMilestoneFormInput = z.input<typeof editMilestoneSchema>;
  type EditMilestoneFormData = z.output<typeof editMilestoneSchema>;

  const editForm = useForm<EditMilestoneFormInput, any, EditMilestoneFormData>({
    resolver: zodResolver(editMilestoneSchema),
    defaultValues: {
      title: milestone.title,
      dueDate: milestone.dueDate ? formatDateInput(new Date(milestone.dueDate)) : "",
    },
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: milestone.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Fetch categories for the category selector (include goals-only categories)
  const { data: rawCategories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });
  // The API can return a general and a goal-scoped category with the same
  // name (e.g. "Health"); these task pickers show a flat list, so collapse
  // duplicates by name (first occurrence wins — lowest sortOrder).
  const categories = useMemo(() => {
    const seen = new Set<string>();
    return rawCategories.filter((c) => {
      const key = (c.name ?? "").toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rawCategories]);

  // Fetch tasks for this milestone when expanded
  const { data: tasks = [], isLoading: tasksLoading } = useQuery<Task[]>({
    queryKey: ["/api/milestones", milestone.id, "tasks"],
    enabled: isExpanded,
  });

  // Fetch all sub-tasks to calculate progress for each task
  const { data: allSubTasks = [] } = useQuery<SubTask[]>({
    queryKey: ["/api/subtasks/all"],
  });

  // Group sub-tasks by task ID
  const subTasksByTaskId = useMemo(() => {
    const grouped = new Map<string, SubTask[]>();
    allSubTasks.forEach((subTask) => {
      if (!grouped.has(subTask.taskId)) {
        grouped.set(subTask.taskId, []);
      }
      grouped.get(subTask.taskId)!.push(subTask);
    });
    return grouped;
  }, [allSubTasks]);

  // Calculate sub-task progress
  const getSubTaskProgress = (taskId: string) => {
    const taskSubTasks = subTasksByTaskId.get(taskId) || [];
    if (taskSubTasks.length === 0) return null;

    const completed = taskSubTasks.filter((st) => st.completed).length;
    const total = taskSubTasks.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  };

  // Fetch unlinked tasks from the same goal for linking
  const { data: allGoalTasks = [] } = useQuery<Task[]>({
    queryKey: ["/api/tasks"],
    enabled: addTaskDialogOpen,
  });

  // Filter unlinked tasks (tasks that belong to the goal but not to any milestone)
  const unlinkedTasks = allGoalTasks.filter(
    (task) => task.goalId === goalId && !task.milestoneId
  );

  const completedCount = tasks.filter((t) => t.completed).length;
  const totalCount = tasks.length;

  // Idempotency key: generated once per submission attempt, reused on
  // network-error retries so the server can deduplicate. Cleared after a
  // confirmed success or when the dialog closes without submitting.
  const createTaskIdempotencyKeyRef = useRef<string | null>(null);

  // When the add-task dialog closes (cancelled or after success), discard any
  // pending idempotency key so the next open starts a fresh submission.
  useEffect(() => {
    if (!addTaskDialogOpen) {
      createTaskIdempotencyKeyRef.current = null;
    }
  }, [addTaskDialogOpen]);

  // Create new task mutation
  const createTaskMutation = useMutation({
    mutationFn: async (data: TaskFormData) => {
      if (!createTaskIdempotencyKeyRef.current) {
        createTaskIdempotencyKeyRef.current = crypto.randomUUID();
      }
      const response = await apiRequest(
        "POST",
        `/api/milestones/${milestone.id}/tasks`,
        {
          title: data.title,
          description: data.description || undefined,
          dueDate: data.dueDate || undefined,
          showOnMainTaskPage: data.showOnMainTaskPage,
          category: data.category || "Uncategorized",
          goalId: goalId,
        },
        { "Idempotency-Key": createTaskIdempotencyKeyRef.current }
      );
      const taskData = await response.json();

      // Create subtasks if any
      if (subtasks.length > 0) {
        await Promise.all(
          subtasks.map((subtaskText, index) =>
            apiRequest("POST", `/api/tasks/${taskData.id}/subtasks`, {
              text: subtaskText,
              displayOrder: index,
            })
          )
        );
      }

      return taskData;
    },
    onSuccess: () => {
      createTaskIdempotencyKeyRef.current = null;
      queryClient.invalidateQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setAddTaskDialogOpen(false);
      form.reset();
      setSubtasks([]);
      setNewSubtask("");
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      toastTaskMutationError("Couldn't create task", error, "The task was not created.");
    },
  });

  // Link existing task mutation
  const linkTaskMutation = useMutation({
    mutationFn: (taskId: string) =>
      apiRequest("POST", `/api/milestones/${milestone.id}/tasks/link`, { taskId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      setAddTaskDialogOpen(false);
      linkForm.reset();
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      toastTaskMutationError("Couldn't link task", error, "The task was not linked.");
    },
  });

  // Edit milestone mutation (only used when onEdit is not provided)
  const editMilestoneMutation = useMutation({
    mutationFn: (data: EditMilestoneFormData) =>
      apiRequest("PATCH", `/api/milestones/${milestone.id}`, {
        title: data.title,
        dueDate: data.dueDate || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goalId, "milestones"] });
      setEditDialogOpen(false);
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goalId, "milestones"] });
      toastTaskMutationError("Couldn't save milestone", error, "Your changes were not saved.");
    },
  });

  // Send milestone itself as a task to the main task page
  const sendMilestoneToTasksMutation = useMutation({
    mutationFn: async () => {
      const idempotencyKey = crypto.randomUUID();
      const response = await apiRequest(
        "POST",
        "/api/tasks",
        {
          title: milestone.title,
          goalId: goalId,
          milestoneId: milestone.id,
          showOnMainTaskPage: true,
          category: "Uncategorized",
        },
        { "Idempotency-Key": idempotencyKey }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setMilestoneSentToTasks(true);
      toast({
        title: "Added to Tasks",
        description: `"${milestone.title}" is now on your task page.`,
      });
    },
    onError: (error: Error) => {
      toastTaskMutationError("Couldn't add to Tasks", error, "The task was not created.");
    },
  });

  // Show an existing milestone task on the main task page
  const showTaskOnMainPageMutation = useMutation({
    mutationFn: async ({ taskId }: { taskId: string }) => {
      const response = await apiRequest("PATCH", `/api/tasks/${taskId}`, {
        showOnMainTaskPage: true,
      });
      return response.json();
    },
    onSuccess: (_data, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/milestones", milestone.id, "tasks"],
      });
      toast({
        title: "Added to Tasks",
        description: "This task now appears on your task page.",
      });
      // Optimistically mark the task in the local cache
      queryClient.setQueryData(
        ["/api/milestones", milestone.id, "tasks"],
        (old: Task[] | undefined) =>
          old?.map((t) => (t.id === taskId ? { ...t, showOnMainTaskPage: true } : t))
      );
    },
    onError: (error: Error) => {
      toastTaskMutationError("Couldn't update task", error, "The task was not updated.");
    },
  });

  // Toggle task completion mutation with optimistic updates
  const toggleTaskMutation = useMutation({
    mutationFn: async ({ taskId, completed }: { taskId: string; completed: boolean }) =>
      apiRequest("PATCH", `/api/tasks/${taskId}`, { completed }),
    onMutate: async ({ taskId, completed }) => {
      const isCurrentSession = capturePersonalCacheBoundary();
      await queryClient.cancelQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
      await queryClient.cancelQueries({ queryKey: ["/api/tasks"] });
      if (!isCurrentSession()) return { isCurrentSession };

      const previousMilestoneTasks = queryClient.getQueryData([
        "/api/milestones",
        milestone.id,
        "tasks",
      ]);
      const previousGlobalTasks = queryClient.getQueryData(["/api/tasks"]);

      queryClient.setQueryData(
        ["/api/milestones", milestone.id, "tasks"],
        (old: Task[] | undefined) =>
          old?.map((t) => (t.id === taskId ? { ...t, completed } : t))
      );
      queryClient.setQueryData(["/api/tasks"], (old: Task[] | undefined) =>
        old?.map((t) => (t.id === taskId ? { ...t, completed } : t))
      );

      return { previousMilestoneTasks, previousGlobalTasks, isCurrentSession };
    },
    onError: (err, _variables, context) => {
      if (context && !context.isCurrentSession()) return;
      if (context?.previousMilestoneTasks) {
        queryClient.setQueryData(
          ["/api/milestones", milestone.id, "tasks"],
          context.previousMilestoneTasks
        );
      }
      if (context?.previousGlobalTasks) {
        queryClient.setQueryData(["/api/tasks"], context.previousGlobalTasks);
      }
      toastTaskMutationError("Couldn't update task", err, "Your change was not saved.");
    },
    onSuccess: (_result, _variables, context) => {
      if (context && !context.isCurrentSession()) return;
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
    },
  });

  const handleCreateTask = (data: TaskFormData) => {
    createTaskMutation.mutate(data);
  };

  const handleAddSubtask = () => {
    if (newSubtask.trim()) {
      setSubtasks([...subtasks, newSubtask.trim()]);
      setNewSubtask("");
    }
  };

  const handleRemoveSubtask = (index: number) => {
    setSubtasks(subtasks.filter((_, i) => i !== index));
  };

  const handleLinkTask = (data: LinkTaskFormData) => {
    linkTaskMutation.mutate(data.taskId);
  };

  const handleToggleTask = (taskId: string, completed: boolean) => {
    toggleTaskMutation.mutate({ taskId, completed });
  };

  const handleEditMilestone = (data: EditMilestoneFormData) => {
    editMilestoneMutation.mutate(data);
  };

  // Handle task click to open full-page dialog
  const handleTaskClick = (task: Task, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest("input") ||
      target.closest('[role="checkbox"]')
    ) {
      return;
    }
    setSelectedTask(task);
  };

  // Mutation for creating subtask from the dialog
  const createDialogSubTaskMutation = useMutation({
    mutationFn: async ({ taskId, text }: { taskId: string; text: string }) => {
      const response = await apiRequest("POST", `/api/tasks/${taskId}/subtasks`, { text });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
      setDialogSubTaskText("");
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      toastTaskMutationError("Couldn't create sub-task", error, "The sub-task was not created.");
    },
  });

  // Mutation for toggling subtask from the dialog
  const toggleDialogSubTaskMutation = useMutation({
    mutationFn: async ({ id, completed }: { id: string; completed: boolean }) => {
      const response = await apiRequest("PATCH", `/api/subtasks/${id}`, { completed });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      toastTaskMutationError("Couldn't update sub-task", error, "The sub-task was not updated.");
    },
  });

  // Mutation for deleting subtask from the dialog
  const deleteDialogSubTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/subtasks/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      toastTaskMutationError("Couldn't delete sub-task", error, "The sub-task was not deleted.");
    },
  });

  // Mutation for updating task from the dialog
  const updateTaskMutation = useMutation({
    mutationFn: async (data: TaskFormData & { taskId: string }) => {
      const { taskId, ...updates } = data;
      const response = await apiRequest("PATCH", `/api/tasks/${taskId}`, {
        title: updates.title,
        description: updates.description || undefined,
        dueDate: updates.dueDate || undefined,
        category: updates.category || "Uncategorized",
        showOnMainTaskPage: updates.showOnMainTaskPage,
      });
      return response.json();
    },
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/milestones", milestone.id, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setSelectedTask(updatedTask);
      toast({
        title: "Task updated",
        description: "The task has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toastTaskMutationError(
        "Couldn't update task",
        error,
        "Failed to update task. Please try again."
      );
    },
  });

  const handleAddDialogSubTask = () => {
    if (createDialogSubTaskMutation.isPending) return;
    if (!selectedTask) return;
    const text = dialogSubTaskText.trim();
    if (text) {
      createDialogSubTaskMutation.mutate({ taskId: selectedTask.id, text });
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-500/10 text-red-700 dark:text-red-300 border-red-300";
      case "high":
        return "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-300";
      case "medium":
        return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-300";
      case "low":
        return "bg-green-500/10 text-green-700 dark:text-green-300 border-green-300";
      default:
        return "bg-gray-500/10 text-gray-700 dark:text-gray-300 border-gray-300";
    }
  };

  return (
    <>
      <div ref={setNodeRef} style={style} className="flex flex-col gap-2">
        <div
          className="flex items-center gap-2 p-2 bg-muted/30 rounded-md group hover:bg-muted/50"
          data-testid={`milestone-item-${milestone.id}`}
        >
          <div
            {...attributes}
            {...listeners}
            className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 focus-visible:opacity-100 transition-opacity"
            data-testid={`drag-handle-milestone-${milestone.id}`}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid={`button-expand-milestone-${milestone.id}`}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </Button>

          <Checkbox
            id={`milestone-${milestone.id}`}
            checked={milestone.achieved}
            onCheckedChange={(checked) => onToggle(milestone.id, checked as boolean)}
            data-testid={`checkbox-milestone-${milestone.id}`}
          />

          <label
            htmlFor={`milestone-${milestone.id}`}
            className={`flex-1 text-sm cursor-pointer ${
              milestone.achieved ? "text-muted-foreground line-through" : "text-foreground"
            }`}
          >
            {milestone.title}
          </label>

          {totalCount > 0 && (
            <Badge
              variant="secondary"
              className="text-xs"
              data-testid={`badge-task-count-${milestone.id}`}
            >
              {completedCount}/{totalCount} tasks
            </Badge>
          )}

          {milestone.dueDate && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(milestone.dueDate)}</span>
            </div>
          )}

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setAddTaskDialogOpen(true)}
              data-testid={`button-add-task-${milestone.id}`}
            >
              <Plus className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`h-6 w-6 ${milestoneSentToTasks ? "text-green-600" : ""}`}
              onClick={() => !milestoneSentToTasks && sendMilestoneToTasksMutation.mutate()}
              disabled={sendMilestoneToTasksMutation.isPending || milestoneSentToTasks}
              title={milestoneSentToTasks ? "Already added to task page" : "Add milestone as task to task page"}
              data-testid={`button-send-milestone-to-tasks-${milestone.id}`}
            >
              {milestoneSentToTasks ? (
                <Check className="w-3 h-3" />
              ) : (
                <ListPlus className="w-3 h-3" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => (onEdit ? onEdit(milestone) : setEditDialogOpen(true))}
              data-testid={`button-edit-milestone-${milestone.id}`}
            >
              <Pencil className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-destructive hover:text-destructive"
              onClick={() => onDelete(milestone.id)}
              data-testid={`button-delete-milestone-${milestone.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {isExpanded && (
          <div className="ml-12 space-y-2">
            {tasksLoading ? (
              <div className="text-sm text-muted-foreground py-2">Loading tasks...</div>
            ) : tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 border border-dashed border-border rounded-lg bg-muted/20">
                <ListTodo className="h-10 w-10 text-muted-foreground mb-3 opacity-50" />
                <p className="text-sm font-medium text-foreground mb-1">No tasks yet</p>
                <p className="text-xs text-muted-foreground mb-4">
                  Add tasks to track progress for this milestone
                </p>
                <Button
                  onClick={() => setAddTaskDialogOpen(true)}
                  size="sm"
                  className="gap-2"
                  data-testid={`button-add-first-task-${milestone.id}`}
                >
                  <Plus className="h-4 w-4" />
                  Add Task
                </Button>
              </div>
            ) : (
              <>
                {tasks.map((task) => {
                  const progress = getSubTaskProgress(task.id);

                  return (
                    <div
                      key={task.id}
                      className="flex flex-col gap-2 p-3 bg-muted/20 rounded-md hover:bg-muted/50 border border-border/50 cursor-pointer transition-colors group/task"
                      data-testid={`task-item-${task.id}`}
                      onClick={(e) => handleTaskClick(task, e)}
                    >
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id={`task-${task.id}`}
                          checked={task.completed}
                          onCheckedChange={(checked) =>
                            handleToggleTask(task.id, checked as boolean)
                          }
                          className="mt-0.5"
                          data-testid={`checkbox-task-${task.id}`}
                        />

                        <div className="flex-1 space-y-2">
                          <label
                            htmlFor={`task-${task.id}`}
                            className={`text-sm font-medium cursor-pointer block ${
                              task.completed
                                ? "text-muted-foreground line-through"
                                : "text-foreground"
                            }`}
                          >
                            {task.title}
                          </label>

                          <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                            {task.createdAt && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>Start: {formatDate(task.createdAt)}</span>
                              </div>
                            )}

                            {task.dueDate && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>Deadline: {formatDate(task.dueDate)}</span>
                              </div>
                            )}

                            {progress ? (
                              <div className="flex items-center gap-1">
                                <ListTodo className="w-3 h-3" />
                                <span>
                                  Progress: {progress.percentage}% ({progress.completed}/
                                  {progress.total} sub-tasks)
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-muted-foreground/60">
                                <ListTodo className="w-3 h-3" />
                                <span>No sub-tasks</span>
                              </div>
                            )}
                          </div>

                          {progress && (
                            <Progress value={progress.percentage} className="h-1.5" />
                          )}
                        </div>

                        {/* Add-to-task-page button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-6 w-6 shrink-0 self-start transition-opacity ${
                            task.showOnMainTaskPage
                              ? "text-green-600 opacity-0 group-hover/task:opacity-100"
                              : "opacity-0 group-hover/task:opacity-100"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!task.showOnMainTaskPage) {
                              showTaskOnMainPageMutation.mutate({ taskId: task.id });
                            }
                          }}
                          disabled={task.showOnMainTaskPage || showTaskOnMainPageMutation.isPending}
                          title={
                            task.showOnMainTaskPage
                              ? "Already on task page"
                              : "Add to task page"
                          }
                          data-testid={`button-show-task-on-main-page-${task.id}`}
                        >
                          {task.showOnMainTaskPage ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <ListPlus className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}

                {/* Add task button at bottom of list */}
                <Button
                  onClick={() => setAddTaskDialogOpen(true)}
                  variant="outline"
                  size="sm"
                  className="w-full gap-2 mt-2"
                  data-testid={`button-add-another-task-${milestone.id}`}
                >
                  <Plus className="h-4 w-4" />
                  Add Another Task
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Built-in Edit Milestone Dialog (only when no external onEdit handler) */}
      {!onEdit && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Milestone</DialogTitle>
            </DialogHeader>

            <Form {...editForm}>
              <form
                onSubmit={editForm.handleSubmit(handleEditMilestone)}
                className="space-y-4"
              >
                <FormField
                  control={editForm.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter milestone title"
                          {...field}
                          data-testid="input-edit-milestone-title"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="dueDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Due Date</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={field.value || ""}
                          data-testid="input-edit-milestone-due-date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditDialogOpen(false)}
                    data-testid="button-cancel-edit-milestone"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={editMilestoneMutation.isPending}
                    data-testid="button-save-edit-milestone"
                  >
                    {editMilestoneMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Task Dialog */}
      <Dialog open={addTaskDialogOpen} onOpenChange={setAddTaskDialogOpen}>
        <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Task to {milestone.title}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Create a new task or link an existing task from this goal
            </p>
          </DialogHeader>

          <Tabs defaultValue="create" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="create" className="gap-2">
                <Plus className="h-4 w-4" />
                Create New Task
              </TabsTrigger>
              <TabsTrigger value="link" className="gap-2">
                <ListTodo className="h-4 w-4" />
                Link Existing Task
              </TabsTrigger>
            </TabsList>

            <TabsContent value="create" className="space-y-4 mt-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleCreateTask)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter task title"
                            {...field}
                            data-testid="input-task-title"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter task description"
                            rows={3}
                            {...field}
                            value={field.value || ""}
                            data-testid="input-task-description"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dueDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Due Date</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            value={field.value || ""}
                            data-testid="input-task-due-date"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-task-category">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                            {categories.filter((c) => c.name).map((category) => (
                              <SelectItem key={category.id} value={category.name!}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-3">
                    <FormLabel>Subtasks (Optional)</FormLabel>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add subtask (optional)"
                        value={newSubtask}
                        onChange={(e) => setNewSubtask(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddSubtask();
                          }
                        }}
                        data-testid="input-add-subtask"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleAddSubtask}
                        data-testid="button-add-subtask"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {subtasks.length > 0 && (
                      <div className="space-y-2">
                        {subtasks.map((subtask, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-2 bg-muted/30 rounded-md"
                          >
                            <span
                              className="flex-1 text-sm"
                              data-testid={`text-subtask-${index}`}
                            >
                              {subtask}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleRemoveSubtask(index)}
                              data-testid={`button-remove-subtask-${index}`}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="showOnMainTaskPage"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                        <div className="space-y-0.5">
                          <FormLabel>Show on Main Task Page</FormLabel>
                          <div className="text-sm text-muted-foreground">
                            When disabled, this task will only appear within this goal
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="switch-show-on-main-task-page"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAddTaskDialogOpen(false)}
                      data-testid="button-cancel-task"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={createTaskMutation.isPending}
                      data-testid="button-create-task"
                    >
                      {createTaskMutation.isPending ? "Creating..." : "Create Task"}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </TabsContent>

            <TabsContent value="link" className="space-y-4 mt-4">
              {unlinkedTasks.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  No unlinked tasks available for this goal
                </div>
              ) : (
                <Form {...linkForm}>
                  <form
                    onSubmit={linkForm.handleSubmit(handleLinkTask)}
                    className="space-y-4"
                  >
                    <FormField
                      control={linkForm.control}
                      name="taskId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Select Task to Link</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-link-task">
                                <SelectValue placeholder="Choose a task to link" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {unlinkedTasks.map((task) => (
                                <SelectItem
                                  key={task.id}
                                  value={task.id}
                                  data-testid={`select-option-task-${task.id}`}
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">{task.title}</span>
                                    {task.description && (
                                      <span className="text-xs text-muted-foreground">
                                        {task.description}
                                      </span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setAddTaskDialogOpen(false)}
                        data-testid="button-cancel-link"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={linkTaskMutation.isPending}
                        data-testid="button-link-task"
                      >
                        {linkTaskMutation.isPending ? "Linking..." : "Link Task"}
                      </Button>
                    </DialogFooter>
                  </form>
                </Form>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Full-Page Task Detail Dialog */}
      <Dialog
        open={!!selectedTask}
        onOpenChange={(open) => !open && setSelectedTask(null)}
      >
        <DialogContent
          className="h-screen w-screen m-0 p-0 rounded-none flex flex-col"
          data-testid="dialog-task-detail"
        >
          <Form {...taskEditForm}>
            <form
              onSubmit={taskEditForm.handleSubmit((data) => {
                if (selectedTask) {
                  updateTaskMutation.mutate({ ...data, taskId: selectedTask.id });
                }
              })}
              className="flex flex-col h-full overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-start justify-between p-3 sm:p-4 md:p-6 border-b shrink-0">
                <div className="flex-1 pr-4 sm:pr-6 md:pr-8">
                  <FormField
                    control={taskEditForm.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            className="text-xl sm:text-2xl md:text-3xl font-bold border-none shadow-none focus-visible:ring-0 px-0 h-auto"
                            data-testid="dialog-input-task-title"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {selectedTask?.completed && (
                      <Badge
                        variant="outline"
                        className="bg-green-500/10 text-green-700 dark:text-green-300"
                        data-testid="dialog-task-completed"
                      >
                        Completed
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedTask(null)}
                  data-testid="button-close-task-dialog"
                  className="shrink-0 min-h-[44px] min-w-[44px]"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
                <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
                  {/* Category Selector */}
                  <FormField
                    control={taskEditForm.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="dialog-select-category">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                            {categories.filter((c) => c.name).map((category) => (
                              <SelectItem key={category.id} value={category.name!}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Description */}
                  <FormField
                    control={taskEditForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value || ""}
                            placeholder="Task description..."
                            className="min-h-[100px]"
                            data-testid="dialog-input-task-description"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedTask?.createdAt && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">Created</p>
                          <p
                            className="text-sm text-muted-foreground"
                            data-testid="dialog-task-created"
                          >
                            {formatDate(selectedTask.createdAt)}
                          </p>
                        </div>
                      </div>
                    )}
                    <FormField
                      control={taskEditForm.control}
                      name="dueDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Due Date</FormLabel>
                          <FormControl>
                            <Input
                              type="date"
                              {...field}
                              data-testid="dialog-input-task-due-date"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Show on Main Task Page Toggle */}
                  <FormField
                    control={taskEditForm.control}
                    name="showOnMainTaskPage"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Show on Main Task Page</FormLabel>
                          <div className="text-sm text-muted-foreground">
                            When enabled, this task will appear on the main Tasks page
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="dialog-switch-show-on-main-task-page"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Sub-tasks */}
                  {selectedTask && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Sub-tasks</h3>
                        {(() => {
                          const progress = getSubTaskProgress(selectedTask.id);
                          if (progress) {
                            return (
                              <span
                                className="text-sm text-muted-foreground"
                                data-testid="dialog-subtask-progress"
                              >
                                {progress.completed}/{progress.total} completed (
                                {progress.percentage}%)
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>

                      {/* Progress Bar */}
                      {(() => {
                        const progress = getSubTaskProgress(selectedTask.id);
                        if (progress) {
                          return (
                            <div className="mb-4">
                              <Progress
                                value={progress.percentage}
                                className="h-2"
                                data-testid="dialog-progress-bar"
                              />
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Sub-task List */}
                      <div className="space-y-2 mb-4">
                        {(() => {
                          const taskSubTasks = subTasksByTaskId.get(selectedTask.id) || [];
                          if (taskSubTasks.length === 0) {
                            return (
                              <p
                                className="text-sm text-muted-foreground py-4 text-center"
                                data-testid="dialog-no-subtasks"
                              >
                                No sub-tasks yet. Add one below!
                              </p>
                            );
                          }
                          return taskSubTasks.map((subTask) => (
                            <div
                              key={subTask.id}
                              className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                              data-testid={`dialog-subtask-item-${subTask.id}`}
                            >
                              <Checkbox
                                checked={subTask.completed}
                                onCheckedChange={(checked) => {
                                  toggleDialogSubTaskMutation.mutate({
                                    id: subTask.id,
                                    completed: checked as boolean,
                                  });
                                }}
                                className="mt-0.5"
                                data-testid={`dialog-checkbox-subtask-${subTask.id}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`break-words ${
                                    subTask.completed
                                      ? "line-through text-muted-foreground"
                                      : ""
                                  }`}
                                  data-testid={`dialog-text-subtask-${subTask.id}`}
                                >
                                  {subTask.text}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="min-h-[44px] min-w-[44px] text-destructive shrink-0"
                                onClick={() =>
                                  deleteDialogSubTaskMutation.mutate(subTask.id)
                                }
                                data-testid={`dialog-button-delete-subtask-${subTask.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ));
                        })()}
                      </div>

                      {/* Add Sub-task */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <Input
                          placeholder="Add a new sub-task..."
                          value={dialogSubTaskText}
                          onChange={(e) => setDialogSubTaskText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddDialogSubTask();
                            }
                          }}
                          className="flex-1 min-h-[44px]"
                          data-testid="dialog-input-new-subtask"
                        />
                        <Button
                          onClick={handleAddDialogSubTask}
                          disabled={
                            !dialogSubTaskText.trim() ||
                            createDialogSubTaskMutation.isPending
                          }
                          data-testid="dialog-button-add-subtask"
                          className="w-full sm:w-auto min-h-[44px]"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Sub-task
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer with Save Button */}
              <div className="border-t p-3 sm:p-4 md:p-6 flex flex-col sm:flex-row justify-end gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedTask(null)}
                  data-testid="dialog-button-cancel"
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateTaskMutation.isPending}
                  data-testid="dialog-button-save"
                  className="w-full sm:w-auto"
                >
                  {updateTaskMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
